// routes/components/ProtectedRoute.tsx
/**
 * ProtectedRoute - Wrapper for routes that require authentication
 * Supports both new accountType system and legacy role system
 *
 * @param requiredRoles - Legacy: Base roles allowed
 * @param requiredAccountTypes - New: Account types allowed (admin, store, staff, user)
 * @param requiredTournamentRoles - New: Tournament-specific roles allowed
 * @param allowHeadJudgeTeam - If true, also allows users with headJudgeTeamAssignments
 * @param allowActingLead - If true, also allows users who are acting team leads
 */

import { ReactElement, cloneElement, isValidElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLoading } from '../../components/common/Loading';
import { LastNamePromptDialog } from '../../components/auth/LastNamePromptDialog';
import { ROUTES } from '../../config/routes';
import { getEffectiveRoleNames, type UserWithRole } from '../../utils/permissions';
import type { ProtectedRouteProps } from '../types';
import type { AccountType, TournamentRole } from '../../types/roles.types';

export function ProtectedRoute({
  children,
  requiredRoles = [],
  requiredAccountTypes = [],
  requiredTournamentRoles = [],
  allowHeadJudgeTeam = false,
  allowActingLead = false,
  allowFloorTeam = false,
}: ProtectedRouteProps): ReactElement {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return <PageLoading message="Authenticating..." />;
  }

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Admin always has access
  if (isAdmin()) {
    if (isValidElement(children)) {
      return cloneElement(children, { user } as { user: typeof user });
    }
    return <>{children}</>;
  }

  let hasAccess = false;

  // Check new account types first
  if (requiredAccountTypes.length > 0) {
    const userAccountType = user.accountType as AccountType | undefined;
    if (userAccountType && requiredAccountTypes.includes(userAccountType)) {
      hasAccess = true;
    }
  }

  // Check new tournament roles
  if (!hasAccess && requiredTournamentRoles.length > 0) {
    // Note: tournamentRole should be set by the component/hook that knows the current tournament
    const userTournamentRole = (user as { tournamentRole?: TournamentRole }).tournamentRole;
    if (userTournamentRole && requiredTournamentRoles.includes(userTournamentRole)) {
      hasAccess = true;
    }
  }

  // Legacy: Check old role system
  if (!hasAccess && requiredRoles.length > 0) {
    if (user.role && requiredRoles.includes(user.role)) {
      hasAccess = true;
    }
  }

  // Per-tournament assignment role (from staffAssignments), honoring the
  // special-floor override. Additive — only grants access, never removes it.
  // This is what lets a user assigned the "Head Judge" (or Manager, etc.)
  // staff role reach Head-Judge-level pages, matching the nav menu.
  if (!hasAccess && requiredRoles.length > 0) {
    const effectiveRoles = getEffectiveRoleNames(user as unknown as UserWithRole);
    if (requiredRoles.some((r) => effectiveRoles.includes(r))) {
      hasAccess = true;
    }
  }

  // If no requirements specified, allow any authenticated user
  if (requiredRoles.length === 0 && requiredAccountTypes.length === 0 && requiredTournamentRoles.length === 0) {
    hasAccess = true;
  }

  // Check for head judge team assignment (temp HJ)
  if (!hasAccess && allowHeadJudgeTeam) {
    const hasHJTeamAssignment =
      user.headJudgeTeamAssignments && Object.keys(user.headJudgeTeamAssignments).length > 0;
    if (hasHJTeamAssignment) {
      hasAccess = true;
    }
  }

  // Check for acting lead (assistant when team lead is on break)
  if (!hasAccess && allowActingLead) {
    const isActingLead =
      user.actingTeamLeadFor && Object.keys(user.actingTeamLeadFor as Record<string, unknown>).length > 0;
    if (isActingLead) {
      hasAccess = true;
    }
  }

  // Check for floor team assignment (user is on any floor team)
  if (!hasAccess && allowFloorTeam) {
    const hasFloorTeamAssignment =
      user.floorTeamAssignments && Object.keys(user.floorTeamAssignments).length > 0;
    if (hasFloorTeamAssignment) {
      hasAccess = true;
    }
  }

  if (!hasAccess) {
    return <Navigate to={ROUTES.LANDING} replace />;
  }

  // Clone children and pass user prop. Render the LastNamePrompt
  // alongside so any approved staff/admin without a surname is
  // blocked until they fill it in — the dialog short-circuits to
  // null for accounts that don't need prompting, so this is a no-op
  // for everyone else.
  if (isValidElement(children)) {
    return (
      <>
        {cloneElement(children, { user } as { user: typeof user })}
        <LastNamePromptDialog />
      </>
    );
  }

  return (
    <>
      {children}
      <LastNamePromptDialog />
    </>
  );
}

export default ProtectedRoute;
